/**
 * Custom-folder membership helpers — pure, the single source of truth.
 *
 * Membership lives on the particle as `folders: { [folderId]: folderName }`.
 * The KEY is the STABLE folder id (`folder.id`, a name-slug set once at
 * creation and never changed on rename); the value is the display name (for
 * labels only). All matching is by the id key so renaming a folder never
 * orphans the mail filed in it.
 */

export type FolderMap = Record<string, string>;

/** True when `folderId` is a key of the particle's folders map (case-insensitive). */
export function particleInFolder(particle: { folders?: FolderMap | null }, folderId: string): boolean {
    const folders = particle?.folders;
    if (!folders) return false;
    const target = folderId.toLowerCase();
    return Object.keys(folders).some((key) => key.toLowerCase() === target);
}

/** Return a NEW folders map with `folderId -> folderName` added (id-keyed). */
export function addFolderToParticle(folders: FolderMap | undefined | null, folderId: string, folderName: string): FolderMap {
    const next: FolderMap = { ...(folders || {}) };
    if (!Object.keys(next).some((k) => k.toLowerCase() === folderId.toLowerCase())) {
        next[folderId] = folderName;
    }
    return next;
}

/**
 * Return a NEW folders map without `folderId`, PRESERVING the real keys of
 * every other entry (the old code rebuilt from values, corrupting the keys).
 */
export function removeFolderFromParticle(folders: FolderMap | undefined | null, folderId: string): FolderMap {
    const next: FolderMap = {};
    const target = folderId.toLowerCase();
    for (const [key, value] of Object.entries(folders || {})) {
        if (key.toLowerCase() !== target) next[key] = value;
    }
    return next;
}

/** Derive a stable folder id from a display name (slug whitespace; keep case). */
export function normalizeFolderId(name: string): string {
    return name.trim().replace(/\s+/g, '_');
}
